import { useQuery } from '@apollo/client';
import { defTitle } from 'assets/strings';
import { GRAPHQL_CATALOGO_ACOES, GRAPHQL_PERFIL_ACOES } from 'gql/perfil';
import React, { FC, useEffect, useMemo, useState } from 'react';
import {
    BotaoGerenciar,
    ContainerPage,
    Estado,
    Lista,
    Painel,
    Topo,
} from './styl';

interface IPerfil {
    id: number;
    nome: string;
    administrador: boolean;
    acoes?: string[];
}

const semAcentos = (t: string) =>
    (t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const ListarPerfil: FC = () => {
    const [busca, setBusca] = useState('');
    const { data, loading, error } = useQuery(GRAPHQL_PERFIL_ACOES, {
        fetchPolicy: 'no-cache',
    });
    const { data: dataCatalogo } = useQuery(GRAPHQL_CATALOGO_ACOES, {
        fetchPolicy: 'no-cache',
    });

    // Mesma base da tela de permissões: só recursos ativos do catálogo.
    const chavesVisualizar = useMemo(
        () =>
            new Set<string>(
                (dataCatalogo?.catalogoAcoes || []).flatMap(r =>
                    r.acoes
                        .filter(a => a.id === 'visualizar')
                        .map(a => a.chave),
                ),
            ),
        [dataCatalogo],
    );

    useEffect(() => {
        document.title = `${defTitle} - Perfis`;
    }, []);

    const perfis: IPerfil[] = useMemo(() => {
        const termo = semAcentos(busca.trim());
        return [...(data?.perfil || [])]
            .filter(
                (p: IPerfil) => !termo || semAcentos(p.nome).includes(termo),
            )
            .sort((a: IPerfil, b: IPerfil) =>
                a.nome.localeCompare(b.nome, 'pt-BR'),
            );
    }, [data, busca]);

    const renderConteudo = () => {
        if (loading) return <Estado>Carregando perfis...</Estado>;
        if (error) {
            return (
                <Estado>
                    Não foi possível carregar os perfis. Recarregue a página.
                </Estado>
            );
        }
        if (perfis.length === 0) {
            return (
                <Estado>
                    {busca
                        ? `Nenhum perfil encontrado para "${busca}".`
                        : 'Nenhum perfil cadastrado.'}
                </Estado>
            );
        }
        return (
            <Lista>
                {perfis.map(perfil => {
                    const total = (perfil.acoes || []).filter(chave =>
                        chavesVisualizar.has(chave),
                    ).length;
                    return (
                        <li key={perfil.id}>
                            <div className="info">
                                <strong>{perfil.nome}</strong>
                                <small>
                                    {perfil.administrador
                                        ? 'Administrador: acessa todos os recursos'
                                        : `${total} ${
                                              total === 1
                                                  ? 'recurso liberado para visualizar'
                                                  : 'recursos liberados para visualizar'
                                          }`}
                                </small>
                            </div>
                            {perfil.administrador ? (
                                <span className="total">Acesso total</span>
                            ) : (
                                <BotaoGerenciar
                                    to={`/admin/perfil/${perfil.id}/permissoes`}
                                >
                                    Gerenciar permissões
                                </BotaoGerenciar>
                            )}
                        </li>
                    );
                })}
            </Lista>
        );
    };

    return (
        <ContainerPage>
            <Painel>
                <Topo>
                    <div>
                        <h1>Perfis de acesso</h1>
                        <p>
                            Escolha um perfil para definir o que os usuários
                            dele podem ver e fazer.
                        </p>
                    </div>
                    <input
                        type="search"
                        placeholder="Buscar perfil"
                        aria-label="Buscar perfil"
                        value={busca}
                        onChange={e => setBusca(e.target.value)}
                    />
                </Topo>
                {renderConteudo()}
            </Painel>
        </ContainerPage>
    );
};
export default ListarPerfil;
